import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';

export default function Departments() {
  const me = useMe();
  // permission flags for this module (from /api/me tree)
  const perm = findPerm(me.modules, 'departments');

  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(null); // { mode:'add'|'edit', dept }
  const [toast, setToast] = useState('');

  async function load() {
    try { setRows(await api('/departments')); }
    catch (e) { setToast(e.message); }
  }
  useEffect(() => { load(); }, []);

  function flash(msg) { setToast(msg); setTimeout(() => setToast(''), 2600); }

  async function remove(dept) {
    if (!window.confirm(`Delete department "${dept.name}"?`)) return;
    try { await api('/departments/' + dept.id, { method: 'DELETE' }); flash('Department deleted'); load(); }
    catch (e) { flash(e.message); }
  }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Masters</div>
          <h1>Departments</h1>
          <div className="count">{rows ? `${rows.length} department${rows.length !== 1 ? 's' : ''}` : 'Loading…'}</div>
        </div>
        {perm.can_create && (
          <button className="btn btn-dark" onClick={() => setModal({ mode: 'add', dept: { name: '' } })}>
            <FiPlus /> Add department
          </button>
        )}
      </div>

      <div className="table-card">
        {!rows ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>No departments yet</h3>
            <p>Create your first department to start organising people.</p>
          </div>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Users</th>
                {(perm.can_edit || perm.can_delete) && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 600 }}>{d.name}</td>
                  <td><span className="pill pill-muted">{d.user_count}</span></td>
                  {(perm.can_edit || perm.can_delete) && (
                    <td>
                      <div className="row-actions">
                        {perm.can_edit && (
                          <button className="btn btn-sm" onClick={() => setModal({ mode: 'edit', dept: d })}>
                            <FiEdit2 /> Edit
                          </button>
                        )}
                        {perm.can_delete && (
                          <button className="btn btn-sm btn-danger" onClick={() => remove(d)}>
                            <FiTrash2 /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <DeptModal
          mode={modal.mode}
          dept={modal.dept}
          onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); flash(msg); load(); }}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function DeptModal({ mode, dept, onClose, onSaved }) {
  const [name, setName] = useState(dept.name || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setError(''); setBusy(true);
    try {
      if (mode === 'add') {
        await api('/departments', { method: 'POST', body: { name } });
        onSaved('Department added');
      } else {
        await api('/departments/' + dept.id, { method: 'PUT', body: { name } });
        onSaved('Department updated');
      }
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{mode === 'add' ? 'Add department' : 'Edit department'}</h2>
        <div className="field">
          <label>Department name</label>
          <input value={name} autoFocus onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && save()}
            placeholder="e.g. Sales" />
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

